import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import UploadingImageSVG from "./svg/UploadingImageSVG";
import BrokenImageSVG from "./svg/BrokenImageSVG";
import EditUserPhotoModel from "../modals/EditUserPhotoModel";

type ImageUploaderProps = {
    onChange: (files: File[] | null) => void;
    deleteImage: () => void;
    imageUrl: string | null;
    onImageBrokenChange?: (isBroken: boolean) => void;
    user?: any;
};

export function ImageUploader({ onChange, deleteImage, imageUrl, onImageBrokenChange }: ImageUploaderProps) {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isImageBroken, setIsImageBroken] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { t } = useTranslation();

    const formatUrl = (url: string | null | undefined) => {
        if (!url) return null;
        if (url.startsWith("data:") || url.startsWith("blob:")) return url;

        const baseUrl = process.env.REACT_APP_BASE_URL;
        return baseUrl ? `${baseUrl}${url}` : url;
    };

    const [existingImageUrl, setExistingImageUrl] = useState<string | null>(formatUrl(imageUrl));

    const handleSetBroken = (broken: boolean) => {
        setIsImageBroken(broken);
        if (onImageBrokenChange) {
            onImageBrokenChange(broken);
        }
    };

    useEffect(() => {
        const formatted = formatUrl(imageUrl);
        setExistingImageUrl(formatted);
        handleSetBroken(false);
        setErrorMessage(null);
    }, [imageUrl]);

    const hasImage = Boolean(existingImageUrl) && !isImageBroken;
    const currentImageUrl = existingImageUrl || "";

    const handleDeleteImage = () => {
        setExistingImageUrl(null);
        handleSetBroken(false);
        setErrorMessage(null);
        deleteImage();
        setIsModalOpen(false);
    };

    const handleModalImageChange = (files: File[] | null) => {
        if (files && files.length > 0) {
            const file = files[0];

            if (file.size === 0) {
                handleSetBroken(true);
                setExistingImageUrl(null);
                setErrorMessage(t("account.errors.corruptedFile", "Файл пошкоджений або порожній. Оберіть інше фото."));
                return;
            }

            if (!file.type.startsWith("image/")) {
                handleSetBroken(true);
                setExistingImageUrl(null);
                setErrorMessage(t("account.errors.invalidType", "Обраний файл не є зображенням."));
                return;
            }

            const previewUrl = URL.createObjectURL(file);
            setExistingImageUrl(previewUrl);
            handleSetBroken(false); 
            setErrorMessage(null);
            onChange(files);
        } else {
            handleDeleteImage();
        }
    };

    const renderImageBlock = () => (
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-10">
            <button
                className="p-4 hover:scale-105 transition-transform duration-100"
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsModalOpen(true);
                }}
            >
                <div className="w-[200px] h-[50px] bg-background-400 text-text-400 hover:bg-background-800 flex items-center justify-between uppercase rounded-md px-5 py-4 text-base font-normal transition-all duration-300 ease-out opacity-90">
                    {t("account.buttonText.editePhoto")}
                    <UploadingImageSVG classname="w-[20px] h-[20px]" fill="#ffffff" strokeWidth={7.5} />
                </div>
            </button>
        </div>
    );

    return (
        <div className="flex flex-col gap-2 items-start w-full">
            {errorMessage && (
                <div className="w-[320px] md:w-[470px] lg:w-[520px] p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm font-medium text-center">
                    {errorMessage}
                </div>
            )}

            {hasImage ? (
                <div className="relative group w-[320px] h-[320px] md:w-[470px] md:h-[470px] lg:w-[520px] lg:h-[520px] overflow-hidden rounded-md">
                    <img
                        src={currentImageUrl}
                        className="object-cover w-full h-full"
                        alt="Avatar"
                        onError={() => handleSetBroken(true)}
                    />
                    {renderImageBlock()}
                </div>
            ) : isImageBroken ? (
                <div
                    onClick={() => setIsModalOpen(true)}
                    className="relative group cursor-pointer w-[320px] h-[320px] md:w-[470px] md:h-[470px] lg:w-[520px] lg:h-[520px] flex flex-col items-center justify-center gap-3 rounded-md overflow-hidden bg-[#F8F8F8] border border-dashed border-gray-300"
                >
                    <BrokenImageSVG className="w-[80px] h-[80px]" />
                    <span className="text-gray-400 text-sm font-medium">
                        {t("account.imageNotFound", "Зображення недоступне")}
                    </span>
                    {renderImageBlock()}
                </div>
            ) : (
                <div
                    onClick={() => setIsModalOpen(true)}
                    className="relative group cursor-pointer w-[320px] h-[320px] md:w-[470px] md:h-[470px] lg:w-[520px] lg:h-[520px] flex items-center justify-center rounded-md overflow-hidden bg-[#F8F8F8]"
                >
                    <UploadingImageSVG classname="w-[100px] h-[80px]" fill="#9F9F9F" strokeWidth={4.5} />
                    {renderImageBlock()}
                </div>
            )}

            <div className="bg-text-400">
                <EditUserPhotoModel
                    isVisible={isModalOpen}
                    setIsVisible={setIsModalOpen}
                    deleteHandler={handleDeleteImage}
                    onChange={handleModalImageChange}
                    imageUrl={currentImageUrl}
                />
            </div>
        </div>
    );
}